import { Action, createAction, handleActions } from 'redux-actions'
import { call, put, select, takeLatest } from 'redux-saga/effects'

import { fetchMobileCarrierCode } from '@/api/BillPaymentApi'
import * as Bridge from '@/api/BridgeApi'
import { LEAVE_ALERT_TYPE } from '@/components/common/layer/Leave/LeaveLayer'
import {
  IBillPaymentGetPulsaProductGroupCodeByPhoneNoResponse,
  IProductType,
  PAYMENT_TYPE_CODE,
} from '@/domains/BillPaymentDomain'
import { BILL_PAYMENT_ROUTES, STATUS_CODE } from '@/domains/CommonDomain'
import { IRootState } from '@/modules'
import { billPaymentAmountActions } from '@/modules/bill-payment/BillPaymentAmountModule'
import { historyActions } from '@/modules/common/history'
import { layerActions } from '@/modules/common/layer'
import { loadingActions } from '@/modules/common/loading'
import { BRIDGE_ERROR_CODE } from '@/utils/bridgeUtils/constants'
import { IContactResult } from '@/utils/bridgeUtils/features/getContact'
import { goBack, push } from '@/utils/historyUtils'

import { errorActions } from '../common/error'
import { billPaymentMainSelector } from './BillPaymentMainModule'

export interface IBillPaymentProductGroupView {
  customerId: string
}

export interface IBillPaymentProductGroupState {
  view: IBillPaymentProductGroupView
}

const initialState: IBillPaymentProductGroupState = {
  view: {
    customerId: '',
  },
}

interface ISelectProductGroupAction {
  paymentTypeCode: string
  productGroupTypeCode: string
}

export const BILL_PAYMENT_PRODUCT_GROUP = 'BillPaymentProductGroup'
export const BILL_PAYMENT_SELECT_PRODUCT_GROUP_FROM_CONTACT = `${BILL_PAYMENT_PRODUCT_GROUP}/selectProductGroupFromContact`

const SELECT_PRODUCT_GROUP_BY_PULSA = `${BILL_PAYMENT_PRODUCT_GROUP}/selectProductGroupByPulsa`
const SET_VIEW = `${BILL_PAYMENT_PRODUCT_GROUP}/setView`
const CLEAR_VIEW = `${BILL_PAYMENT_PRODUCT_GROUP}/clearView`

export const billPaymentProductGroupActions = {
  setView: createAction<Partial<IBillPaymentProductGroupView>>(SET_VIEW),
  selectProductGroupByPulsa: createAction<ISelectProductGroupAction>(SELECT_PRODUCT_GROUP_BY_PULSA),
  selectProductGroupFromContact: createAction(BILL_PAYMENT_SELECT_PRODUCT_GROUP_FROM_CONTACT),
  clearView: createAction(CLEAR_VIEW),
}

const reducer = {
  [SET_VIEW]: (
    state: IBillPaymentProductGroupState,
    action: Action<Partial<IBillPaymentProductGroupView>>,
  ) => ({
    ...state,
    view: {
      ...state.view,
      ...action.payload,
    },
  }),
  [CLEAR_VIEW]: (state: IBillPaymentProductGroupState) => ({
    ...state,
    view: initialState.view,
  }),
}

export const billPaymentProductGroupReducer = handleActions(reducer, initialState)

export const billPaymentProductGroupSelector = {
  view: (state: IRootState) => state[BILL_PAYMENT_PRODUCT_GROUP].view ?? initialState.view,
}

export function* selectProductGroupByPulsa(action: Action<ISelectProductGroupAction>) {
  const { paymentTypeCode, productGroupTypeCode } = action.payload

  const productTypeList: IProductType[] = yield select(
    billPaymentMainSelector.productTypeList,
    paymentTypeCode,
    productGroupTypeCode,
  )

  const { customerId }: IBillPaymentProductGroupView = yield select(
    billPaymentProductGroupSelector.view,
  )

  if (productTypeList.length > 1) {
    yield call(
      push,
      `${BILL_PAYMENT_ROUTES.PRODUCT}/${paymentTypeCode}/${productGroupTypeCode}/${customerId}`,
    )
  } else {
    const productTypeCode = productTypeList[0].code

    yield put(billPaymentAmountActions.setCustomerId(customerId))
    yield call(push, `${BILL_PAYMENT_ROUTES.AMOUNT}/${productTypeCode}`)
  }
}

function* goBackWithoutTransition() {
  yield put(historyActions.nextPagingTransitionOff())
  yield call(goBack)
}

export function* selectProductGroupFromContact() {
  try {
    // In case of pulsa, loading indicator is always displayed except PHONE_NUMBER_NOT_MATCHED case
    yield put(loadingActions.start(BILL_PAYMENT_SELECT_PRODUCT_GROUP_FROM_CONTACT))

    const { result }: IContactResult = yield call(Bridge.getContact)
    const customerId = result.number

    yield put(billPaymentProductGroupActions.setView({ customerId }))

    // 해당 전화번호의 통신사를 서버에서 가져온다
    const {
      productGroupTypeCode,
    }: IBillPaymentGetPulsaProductGroupCodeByPhoneNoResponse = yield call(fetchMobileCarrierCode, {
      phoneNo: customerId,
    })

    const productTypeList: IProductType[] = yield select(
      billPaymentMainSelector.productTypeList,
      PAYMENT_TYPE_CODE.PULSA,
      productGroupTypeCode,
    )

    if (productTypeList.length > 1) {
      yield call(
        push,
        `${BILL_PAYMENT_ROUTES.PRODUCT}/${PAYMENT_TYPE_CODE.PULSA}/${productGroupTypeCode}/${customerId}`,
      )
    } else {
      const productTypeCode = productTypeList[0].code

      yield put(billPaymentAmountActions.setCustomerId(customerId))
      yield call(push, `${BILL_PAYMENT_ROUTES.AMOUNT}/${productTypeCode}`)
    }
  } catch (e) {
    switch (e.code) {
      case BRIDGE_ERROR_CODE.CLOSE:
        yield call(goBackWithoutTransition)
        break

      case STATUS_CODE.PHONE_NUMBER_NOT_MATCHED:
        yield put(layerActions.open(LEAVE_ALERT_TYPE.INVALID_CARRIER))
        // Only for PHONE_NUMBER_NOT_MATCHED case, loading will be finished
        yield put(loadingActions.finish(BILL_PAYMENT_SELECT_PRODUCT_GROUP_FROM_CONTACT))
        break

      case STATUS_CODE.PHONE_NUMBER_INVALID:
      default:
        yield put(errorActions.set(e))
        yield call(goBackWithoutTransition)
        break
    }
  }
}

export const billPaymentProductGroupSaga = [
  takeLatest(SELECT_PRODUCT_GROUP_BY_PULSA, selectProductGroupByPulsa),
  takeLatest(BILL_PAYMENT_SELECT_PRODUCT_GROUP_FROM_CONTACT, selectProductGroupFromContact),
]
